import React, { useState, useEffect } from "react";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function UserComplaints() {
  const [complaintData, setComplaintData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());

  useEffect(() => {
    fetchComplaints();
  }, []);

  const fetchComplaints = async () => {
    try {
      const token = localStorage.getItem("userToken");
      if (!token) {
        setLoading(false);
        return;
      }

      // Fetch all remarks and filter for complaints
      const res = await axios.get(`${BASE_URL}/api/vipremarks`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Extract complaints from the response object
      let complaints = [];
      
      if (res.data && typeof res.data === 'object') {
        // If response is an object with numeric keys
        complaints = Object.values(res.data).filter(item => item.subject === "Complaint");
      } else if (Array.isArray(res.data)) {
        // If response is already an array
        complaints = res.data.filter(item => item.subject === "Complaint");
      }
      
      console.log("Fetched complaints:", complaints); // For debugging
      setComplaintData(complaints);
    } catch (err) {
      console.log("Complaints Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="complaints-loading">
        <div className="spinner"></div>
        <p>Loading complaints...</p>
      </div>
    );
  }

  return (
    <>
      <div className="user-complaints-wrapper">
        {/* Header */}
        <header className="complaints-header">
          <div className="header-content">
            <h1 className="complaints-title">
              <i className="fas fa-exclamation-triangle"></i> Manager Complaints
            </h1>
            <p className="complaints-subtitle">
              View and manage complaints with officer and VIP details
            </p>
          </div>
        </header>

        {/* Complaints Table Card */}
        <section className="complaints-card">
          <div className="table-container">
            <table className="complaints-table">
              <thead>
                <tr>
                  <th>Complaint ID</th>
                  <th>Message / Details</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {complaintData.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '40px' }}>
                      <div className="no-complaints">
                        <i className="fas fa-check-circle" style={{ fontSize: '3rem', color: '#10b981', marginBottom: '1rem' }}></i>
                        <h3>No Complaints Found</h3>
                        <p>There are no complaints submitted yet.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  complaintData.map((item) => (
                    <React.Fragment key={item.id}>
                      <tr className="main-row">
                        <td>
                          <div className="complaint-id-cell">
                            <strong>#{item.id}</strong>
                            <div className="sub-info">
                              <i className="far fa-calendar"></i> {formatDate(item.created)}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="message-details">
                            <p className="remarks">{item.remarks}</p>
                            <div className="subject-info">
                              <span className="subject-badge complaint">
                                <i className="fas fa-exclamation-circle"></i> {item.subject}
                              </span>
                              {item.category && (
                                <span className="vip-info">
                                  <i className="fas fa-user"></i> {item.category.name} ({item.category.designation})
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`status-badge ${item.status?.toLowerCase()}`}>
                            {item.status || 'Pending'}
                          </span>
                        </td>
                        <td>
                          <button
                            className="expand-button"
                            onClick={() => toggleExpand(item.id)}
                          >
                            <i className={`fas ${expandedRows.has(item.id) ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i> 
                            {expandedRows.has(item.id) ? 'Collapse' : 'Expand'}
                          </button>
                        </td>
                      </tr>
                      {expandedRows.has(item.id) && (
                        <tr className="expanded-row">
                          <td colSpan="4">
                            <div className="expanded-details">
                              {/* VIP Details Section */}
                              {item.category && (
                                <div className="detail-section vip-section">
                                  <h4><i className="fas fa-user-tie"></i> VIP Details</h4>
                                  <div className="vip-grid">
                                    <div className="vip-item">
                                      <span className="label">Name:</span>
                                      <span className="value">{item.category.name}</span>
                                    </div>
                                    <div className="vip-item">
                                      <span className="label">Designation:</span>
                                      <span className="value">{item.category.designation}</span>
                                    </div>
                                    <div className="vip-item">
                                      <span className="label">Email:</span>
                                      <span className="value">{item.category.email}</span>
                                    </div>
                                    <div className="vip-item">
                                      <span className="label">Contact:</span>
                                      <span className="value">{item.category.contactno}</span>
                                    </div>
                                    <div className="vip-item">
                                      <span className="label">Gender:</span>
                                      <span className="value">{item.category.gender}</span>
                                    </div>
                                    <div className="vip-item">
                                      <span className="label">Status:</span>
                                      <span className="value status-active">{item.category.status}</span>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Assignment Details Section */}
                              {item.assignment && (
                                <div className="detail-section assignment-section">
                                  <h4><i className="fas fa-tasks"></i> Assignment Details</h4>
                                  <div className="assignment-grid">
                                    <div className="assignment-item">
                                      <span className="label">Assignment ID:</span>
                                      <span className="value">{item.assignment.id}</span>
                                    </div>
                                    <div className="assignment-item">
                                      <span className="label">Status:</span>
                                      <span className="value assignment-status">{item.assignment.status}</span>
                                    </div>
                                    <div className="assignment-item">
                                      <span className="label">Start Time:</span>
                                      <span className="value">{formatDate(item.assignment.startAt)}</span>
                                    </div>
                                    <div className="assignment-item">
                                      <span className="label">End Time:</span>
                                      <span className="value">{formatDate(item.assignment.endAt)}</span>
                                    </div>
                                    <div className="assignment-item">
                                      <span className="label">Assigned At:</span>
                                      <span className="value">{formatDate(item.assignment.assignedAt)}</span>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Officers Details Section */}
                              <div className="detail-section officers-section">
                                <h4><i className="fas fa-user-shield"></i> Assigned Officers ({item.officersRemarks?.length || 0})</h4>
                                {item.officersRemarks && item.officersRemarks.length > 0 ? (
                                  <div className="officers-grid">
                                    {item.officersRemarks.map((officer) => (
                                      <div key={officer.id} className="officer-card">
                                        <div className="officer-header">
                                          <div className="officer-name-rank">
                                            <h5>{officer.name}</h5>
                                            <span className="officer-rank">{officer.rank}</span>
                                          </div>
                                          <span className={`status-badge ${officer.status?.toLowerCase()}`}>
                                            {officer.status}
                                          </span>
                                        </div>
                                        <div className="officer-details">
                                          <div className="officer-detail">
                                            <i className="fas fa-id-card"></i>
                                            <span>ID: {officer.id}</span>
                                          </div>
                                          <div className="officer-detail">
                                            <i className="fas fa-envelope"></i>
                                            <span>{officer.email}</span>
                                          </div>
                                          <div className="officer-detail">
                                            <i className="fas fa-phone"></i>
                                            <span>{officer.contactno}</span>
                                          </div>
                                          <div className="officer-detail">
                                            <i className="fas fa-venus-mars"></i>
                                            <span className={`gender-badge ${officer.gender?.toLowerCase()}`}>
                                              {officer.gender}
                                            </span>
                                          </div>
                                          <div className="officer-detail">
                                            <i className="fas fa-briefcase"></i>
                                            <span>{officer.experience} years exp</span>
                                          </div>
                                          <div className="officer-detail">
                                            <i className="fas fa-fingerprint"></i>
                                            <span>PN: {officer.pnNumber}</span>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="no-officers">
                                    <i className="fas fa-user-slash"></i>
                                    <p>No officers information available</p>
                                  </div>
                                )}
                              </div>

                              {/* Additional Complaint Info */}
                              <div className="detail-section additional-info">
                                <h4><i className="fas fa-info-circle"></i> Additional Information</h4>
                                <div className="additional-grid">
                                  <div className="additional-item">
                                    <span className="label">Complaint Created:</span>
                                    <span className="value">{formatDate(item.created)}</span>
                                  </div>
                                  <div className="additional-item">
                                    <span className="label">Read Time:</span>
                                    <span className="value">{formatDate(item.readtime) || 'Not read yet'}</span>
                                  </div>
                                  <div className="additional-item">
                                    <span className="label">Complaint Status:</span>
                                    <span className={`status-badge ${item.status?.toLowerCase()}`}>
                                      {item.status}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* Premium Dark Glassmorphic Styling */}
      <style jsx>{`
        .user-complaints-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: #e2e8f0;
          padding: 30px 20px;
        }

        .complaints-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .header-content {
          max-width: 800px;
          margin: 0 auto;
        }

        .complaints-title {
          font-size: 2.6rem;
          font-weight: 800;
          color: #ef4444;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          margin-bottom: 12px;
          text-shadow: 0 4px 15px rgba(239, 68, 68, 0.4);
        }

        .complaints-subtitle {
          font-size: 1.2rem;
          opacity: 0.9;
          color: #cbd5e1;
        }

        .complaints-card {
          background: rgba(30, 41, 59, 0.85);
          backdrop-filter: blur(16px);
          border-radius: 28px;
          padding: 40px;
          max-width: 1400px;
          margin: 0 auto;
          border: 1px solid rgba(239, 68, 68, 0.4);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4);
        }

        .table-container {
          overflow-x: auto;
          border-radius: 16px;
        }

        .complaints-table {
          width: 100%;
          border-collapse: collapse;
          color: #e2e8f0;
        }

        .complaints-table th {
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(239, 68, 68, 0.1));
          padding: 18px;
          text-align: left;
          font-weight: 700;
          font-size: 1.1rem;
          color: #fca5a5;
          border-bottom: 2px solid rgba(239, 68, 68, 0.3);
        }

        .complaints-table td {
          padding: 16px;
          text-align: left;
          border-bottom: 1px solid rgba(239, 68, 68, 0.1);
        }

        .main-row:hover {
          background: rgba(239, 68, 68, 0.05);
        }

        .complaint-id-cell {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .complaint-id-cell strong {
          font-size: 1.2rem;
          color: #fca5a5;
        }

        .sub-info {
          font-size: 0.85rem;
          color: #94a3b8;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .message-details {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .remarks {
          font-weight: 500;
          margin: 0;
          color: #e2e8f0;
          line-height: 1.5;
        }

        .subject-info {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .subject-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.85rem;
        }

        .subject-badge.complaint {
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(239, 68, 68, 0.1));
          color: #fca5a5;
          border: 1px solid rgba(239, 68, 68, 0.3);
        }

        .vip-info {
          font-size: 0.9rem;
          color: #94a3b8;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .status-badge {
          display: inline-block;
          padding: 6px 14px;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .status-badge.pending {
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: white;
        }

        .status-badge.resolved {
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
        }

        .status-badge.active {
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
        }

        .status-badge.inactive {
          background: linear-gradient(135deg, #6b7280, #4b5563);
          color: white;
        }

        .expand-button {
          background: linear-gradient(135deg, #ef4444, #dc2626);
          color: white;
          padding: 8px 16px;
          border: none;
          border-radius: 20px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .expand-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(239, 68, 68, 0.5);
        }

        .expanded-row {
          background: rgba(15, 23, 42, 0.2);
        }

        .expanded-details {
          padding: 24px;
          background: rgba(15, 23, 42, 0.6);
          border-radius: 16px;
          margin: 10px 0;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }

        .detail-section {
          margin-bottom: 24px;
          padding-bottom: 24px;
          border-bottom: 1px solid rgba(239, 68, 68, 0.1);
        }

        .detail-section:last-child {
          margin-bottom: 0;
          padding-bottom: 0;
          border-bottom: none;
        }

        .detail-section h4 {
          margin: 0 0 16px 0;
          color: #fca5a5;
          font-size: 1.2rem;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .vip-grid,
        .assignment-grid,
        .additional-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 16px;
          background: rgba(15, 23, 42, 0.4);
          padding: 16px;
          border-radius: 12px;
        }

        .vip-item,
        .assignment-item,
        .additional-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .label {
          font-size: 0.9rem;
          color: #94a3b8;
          font-weight: 500;
        }

        .value {
          font-weight: 600;
          color: #e2e8f0;
        }

        .status-active {
          color: #10b981;
        }

        .assignment-status {
          color: #f59e0b;
        }

        .officers-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 16px;
        }

        .officer-card {
          background: rgba(15, 23, 42, 0.4);
          border-radius: 12px;
          padding: 16px;
          border: 1px solid rgba(239, 68, 68, 0.2);
          transition: all 0.3s ease;
        }

        .officer-card:hover {
          border-color: rgba(239, 68, 68, 0.5);
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(239, 68, 68, 0.2);
        }

        .officer-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
          padding-bottom: 12px;
          border-bottom: 1px solid rgba(239, 68, 68, 0.1);
        }

        .officer-name-rank {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .officer-name-rank h5 {
          margin: 0;
          color: #e2e8f0;
          font-size: 1.1rem;
        }

        .officer-rank {
          font-size: 0.85rem;
          color: #94a3b8;
          font-style: italic;
        }

        .officer-details {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }

        .officer-detail {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9rem;
          color: #cbd5e1;
        }

        .officer-detail i {
          color: #ef4444;
          width: 16px;
        }

        .gender-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 0.8rem;
          text-transform: uppercase;
        }

        .gender-badge.male {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(59, 130, 246, 0.1));
          color: #60a5fa;
          border: 1px solid rgba(59, 130, 246, 0.3);
        }

        .gender-badge.female {
          background: linear-gradient(135deg, rgba(236, 72, 153, 0.2), rgba(236, 72, 153, 0.1));
          color: #f472b6;
          border: 1px solid rgba(236, 72, 153, 0.3);
        }

        .gender-badge.other {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(139, 92, 246, 0.1));
          color: #a78bfa;
          border: 1px solid rgba(139, 92, 246, 0.3);
        }

        .no-complaints,
        .no-officers {
          text-align: center;
          padding: 40px;
          color: #94a3b8;
        }

        .no-complaints h3,
        .no-officers p {
          color: #e2e8f0;
          margin: 8px 0;
        }

        .no-officers {
          background: rgba(15, 23, 42, 0.4);
          border-radius: 12px;
        }

        .no-officers i {
          font-size: 2.5rem;
          margin-bottom: 16px;
          color: #64748b;
        }

        .complaints-loading {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
          text-align: center;
          padding: 40px;
        }

        .spinner {
          width: 70px;
          height: 70px;
          border: 6px solid #1e293b;
          border-top: 6px solid #ef4444;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 20px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .complaints-table th,
          .complaints-table td {
            padding: 12px;
            font-size: 0.9rem;
          }

          .complaints-title {
            font-size: 2.2rem;
          }

          .vip-grid,
          .assignment-grid,
          .additional-grid {
            grid-template-columns: 1fr;
          }

          .officers-grid {
            grid-template-columns: 1fr;
          }

          .officer-details {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .user-complaints-wrapper {
            padding: 15px;
          }

          .complaints-card {
            padding: 20px;
          }

          .complaints-title {
            font-size: 2rem;
          }

          .subject-info {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      `}</style>
    </>
  );
}